import { useMemo, useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  MoneyDisplay,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type Column,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { COUNTRIES } from '@shared/lib/countries';
import { useLogisticsQuoteMutation, type LogisticsQuoteEntry } from '@entities/logistics';

const schema = z.object({
  fromCountry: z.string().regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2'),
  toCountry: z.string().regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2'),
  weightKg: z.number().positive('must be > 0'),
  volumeCbm: z.number().positive('must be > 0'),
});

type CalculatorFormValues = z.infer<typeof schema>;

export function ShipmentCalculator() {
  const { t } = useTranslation();
  const [results, setResults] = useState<LogisticsQuoteEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mutation = useLogisticsQuoteMutation();

  const columns: Column<LogisticsQuoteEntry>[] = useMemo(
    () => [
      { key: 'carrier', header: t('shipment.calculator.columns.carrier'), cell: (q) => q.carrierName },
      { key: 'days', header: t('shipment.calculator.columns.days'), cell: (q) => `${q.estimatedDays} d` },
      { key: 'price', header: t('shipment.calculator.columns.price'), cell: (q) => <MoneyDisplay money={q.price} /> },
    ],
    [t],
  );

  const form = useForm<CalculatorFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      fromCountry: '',
      toCountry: '',
      weightKg: 100,
      volumeCbm: 1,
    },
  });

  const onSubmit = (values: CalculatorFormValues) => {
    setError(null);
    mutation.mutate(values, {
      onSuccess: (data) => setResults(data),
      onError: (err) =>
        setError(err instanceof ApiError ? err.message : 'Quote calculation failed'),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('shipment.calculator.estimate')}</CardTitle>
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 md:grid-cols-2" noValidate>
              <FormField
                label={t('shipment.calculator.from')}
                required
                error={form.formState.errors.fromCountry?.message}
              >
                {() => (
                  <Select
                    value={form.watch('fromCountry') || undefined}
                    onValueChange={(v) => form.setValue('fromCountry', v, { shouldValidate: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('shipment.calculator.originPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.nameEn} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>

              <FormField
                label={t('shipment.calculator.to')}
                required
                error={form.formState.errors.toCountry?.message}
              >
                {() => (
                  <Select
                    value={form.watch('toCountry') || undefined}
                    onValueChange={(v) => form.setValue('toCountry', v, { shouldValidate: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('shipment.calculator.destinationPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.nameEn} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>

              <FormField
                label={t('shipment.calculator.weight')}
                required
                error={form.formState.errors.weightKg?.message}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    step="0.1"
                    min={0.1}
                    {...form.register('weightKg', { valueAsNumber: true })}
                  />
                )}
              </FormField>

              <FormField
                label={t('shipment.calculator.volume')}
                required
                error={form.formState.errors.volumeCbm?.message}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    step="0.01"
                    min={0.01}
                    {...form.register('volumeCbm', { valueAsNumber: true })}
                  />
                )}
              </FormField>

              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" loading={mutation.isPending}>
                  {t('shipment.calculator.calculate')}
                </Button>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      {results.length > 0 ? (
        <DataTable data={results} columns={columns} rowKey={(r) => r.carrierName} />
      ) : null}
    </div>
  );
}
